import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import { GetUrlParams, PaginationParams } from '@lib/enums/urls';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import usePrevious from '@lib/hooks/usePrevious';

type QueryResultLike = {
  loading: boolean;
  data?: Record<string, unknown>;
  previousData?: Record<string, unknown>;
  error?: unknown;
  refetch: () => void;
};

type UseChipListFieldParams<T extends { id: string }> = {
  fieldName: string;
  ids?: string[];
  values: Values;
  paginationParams: PaginationParams;
  setPaginationParams: React.Dispatch<React.SetStateAction<PaginationParams>>;
  queryResult: QueryResultLike;
  newItems: T[];
};

type UseChipListFieldResult<T extends { id: string }> = {
  visibleItems: T[];
  newFormFieldValue: SelectOption[];
  handleRemove: (id: string) => void;
  handleLoadMore: () => void;
  currentPage: number;
  totalPages: number;
  firstLoading: boolean;
  error: unknown;
};

export default function useChipListField<T extends { id: string }>({
  fieldName,
  ids,
  values,
  paginationParams,
  setPaginationParams,
  queryResult,
  newItems,
}: UseChipListFieldParams<T>): UseChipListFieldResult<T> {
  const prevIds = usePrevious(ids);
  const { setValue } = useFormContext();
  const [allItems, setAllItems] = useState<T[]>([]);

  const { loading, previousData, error, refetch } = queryResult;
  const queryData = queryResult.data || previousData;
  const firstLoading = loading && previousData === undefined;
  const metadata = _get(queryData, 'data.metadata');
  const totalPages = _get(metadata, 'totalPages', 1) as number;
  const currentPage = _get(metadata, 'currentPage', 1) as number;

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      setPaginationParams((prev) => ({
        ...prev,
        [GetUrlParams.Page]: `${currentPage + 1}`,
      }));
    }
  };

  const handleRemove = (id: string) => {
    const itemsValue = _get(values, fieldName, []) as SelectOption[];
    const newData = itemsValue.filter(
      ({ value: optionValue }: SelectOption) => optionValue !== id,
    );
    if (fieldName) {
      setValue(fieldName, newData);
    }
  };

  const formFieldValue = (_get(values, fieldName) || []) as SelectOption[];
  const visibleItems = allItems.filter((item) =>
    formFieldValue.some(({ value: optionValue }) => item.id === optionValue),
  );
  const visibleItemsIds = visibleItems.map((item) => item.id);
  let newFormFieldValue = formFieldValue;
  if (ids?.length) {
    newFormFieldValue = formFieldValue.filter(
      (item) => !ids.includes(item.value as string),
    );
  }
  if (visibleItemsIds.length) {
    newFormFieldValue = newFormFieldValue.filter(
      (item) => !visibleItemsIds.includes(item.value as string),
    );
  }

  useEffect(() => {
    if (newItems.length) {
      setAllItems((prevItems) => {
        const map = new Map(prevItems.map((item) => [item.id, item]));
        newItems.forEach((item) => map.set(item.id, item));
        return Array.from(map.values());
      });
    }
  }, [newItems, newItems.length]);

  useEffect(() => {
    if (prevIds === undefined && !_isEqual(ids, prevIds)) {
      setAllItems([]);
      refetch();
    }
  }, [ids, prevIds, refetch]);

  return {
    visibleItems,
    newFormFieldValue,
    handleRemove,
    handleLoadMore,
    currentPage,
    totalPages,
    firstLoading,
    error,
  };
}
